import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CollectionApiClient, IPaginationParameters, IPaginationResponse } from '../../../common';
import { IOrdersApiClient } from './i-orders-api.client';
import {
  ICompleteOrderRequest,
  ICreateSubscriptionOrderRequest,
  ICreateOnStreetReservationOrderRequest,
  IExtendOnStreetReservationOrderRequest,
  IOrderActionRequest,
  IOrderLookupsResponse,
  IOrderPaymentRequestResponse,
  IOrderResponse,
  IOrdersPaginationParameters,
  IOnStreetReservationResponse,
  IOnStreetReservationPaginationParameters,
  IOffStreetOrderResponse,
  ISettleOffStreetReservationOrderRequest,
  ISubscriptionOrderDetailsResponse,
  IOnStreetOrderDetailsResponse,
} from '../dtos';
import { ICreatePublicOnStreetReservationOrderRequest } from '../dtos/requests/create-public-on-street-reservation-order.request';
import { IExtendPublicOnStreetReservationOrderRequest } from '../dtos/requests/extend-public-on-street-reservation-order.request';
import { IPublicOnStreetReservationResponse } from '../dtos/responses/public-on-street-reservation.response';
import { OrderManagementCollections } from '../../common';
import { IPaymentResponse } from '../../../payment-management/payments/dtos/responses';
import { IPublicOffStreetReservationResponse, IReservationResponse } from '../../../customer-management';

@Injectable({
  providedIn: 'root'
})
export class OrdersApiClient implements IOrdersApiClient {
  private collectionApiClient: CollectionApiClient;

  constructor(protected http: HttpClient) {
    this.collectionApiClient = CollectionApiClient.create(OrderManagementCollections.orders, http);
  }

  get(paginationParameters?: IOrdersPaginationParameters): Observable<IPaginationResponse<IOrderResponse>> {
    return this.collectionApiClient.get({
       collectionName: OrderManagementCollections.endUserOrders,
      paginationParameters
    });
  }

  getById(id: string): Observable<IOrderResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.endUserOrder(id),
    });
  }

  getByIdPublic(id: string): Observable<IOrderResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.endUserOrderPublic(id),
    });
  }

  lookups(paginationParameters?: IPaginationParameters): Observable<IOrderLookupsResponse> {
      const params = {
        collectionName: OrderManagementCollections.orderookups(),
        ...(paginationParameters && { paginationParameters })
      };
      return this.collectionApiClient.get(params);
    }

  createSubscriptionOrder(request: ICreateSubscriptionOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.subscriptionOrder,
      body: request
    });
  }

  paymentSubscriptionOrder(id: string): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.paymentSubscriptionOrder(id),
    });
  }

  getSubscriptionOrderDetails(orderId: string): Observable<ISubscriptionOrderDetailsResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.subscriptionOrderDetails(orderId),
    });
  }

  createOnStreetReservationOrder(request: ICreateOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.onStreetReservationOrder,
      body: request
    });
  }

  extendOnStreetReservationOrder(id: string, request: IExtendOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.extendOnStreetReservationOrder(id),
      body: request
    });
  }

  getOnStreetOrderDetails(orderId: string): Observable<IOnStreetOrderDetailsResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.onStreetOrderDetails(orderId),
    });
  }

  getOnStreetReservations(paginationParameters?: IOnStreetReservationPaginationParameters): Observable<IPaginationResponse<IOnStreetReservationResponse>> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.onStreetReservations,
      paginationParameters
    });
  }

  getPayments(orderId: string): Observable<IPaginationResponse<IPaymentResponse>>{
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.payments(orderId),
    });
  }

  getLatestPayment(orderId: string): Observable<IPaymentResponse>{
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.latestPayment(orderId),
    });
  }

  getOffStreetOrders(paginationParameters?: IOrdersPaginationParameters): Observable<IPaginationResponse<IOffStreetOrderResponse>> {
    return this.collectionApiClient.get({
       collectionName: OrderManagementCollections.offStreet,
      paginationParameters
    });
  }

  getByIdOffStreetOrder(id: string): Observable<IOffStreetOrderResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.offStreetOrder(id),
    });
  }

  getOffStreetOrderByCode(code: string): Observable<IOffStreetOrderResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.offStreetOrderByCode(code),
    });
  }

  settleOffStreetOrderForCustomer(code: string, request: ISettleOffStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.settleOffStreetOrderForCustomer(code),
      body: request
    });
  }

  settleOffStreetOrderForAnonymous(code: string, request: ISettleOffStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.settleOffStreetOrderForAnonymous(code),
      body: request
    });
  }

  pickOffStreetOrder(code: string): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.put({
      collectionName: OrderManagementCollections.pickOffStreetOrder(code),
    });
  }

  getPublicOffStreetById(id: string): Observable<IPublicOffStreetReservationResponse>{
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.publicOffStreet(id),
    });
  }

  getPublicOffStreetByCode(code: string): Observable<IPublicOffStreetReservationResponse>{
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.publicOffStreet(code),
    });
  }

  createPublicOnStreetReservationOrder(request: ICreatePublicOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.publicOnStreetReservations,
      body: request
    });
  }

  extendPublicOnStreetReservationOrder(id: string, request: IExtendPublicOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return this.collectionApiClient.post({
      collectionName: OrderManagementCollections.extendPublicOnStreetReservation(id),
      body: request
    });
  }

  getPublicOnStreetReservation(id: string): Observable<IPublicOnStreetReservationResponse> {
    return this.collectionApiClient.get({
      collectionName: OrderManagementCollections.publicOnStreetReservation(id),
    });
  }
}
